import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro';

import './SimRequestsAdmin.scss';
import EditSimRequestDrawer from './EditSimRequestDrawer';
import CommentsDialog from './CommentsDialog';
import NewsIcon from '../../../src/assets/images/news.svg';
import { useSimArtAdminProvider } from '../../providers/SimArtAdminProvider';
import Loader from '../Loader/Loader';
import AcDialog from '../AcDialog/AcDialog';

interface Props {
  position?: any;
}

export default function SimRequestsAdmin({ position }: Props) {
  const { t } = useTranslation();
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);
  const [selectedRowForEdit, setSelectedRowForEdit] = useState<any>(null);
  const [isCommentsDialogOpen, setIsCommentsDialogOpen] = useState(false);
  const [selectedComments, setSelectedComments] = useState<any[]>([]);
  const [selectedRowForComments, setSelectedRowForComments] =
    useState<any>(null);
  const [showModal, setShowModal] = useState(false);
  const [errMsg, setErrMsg] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [commentLoading, setCommentLoading] = useState(false);

  const { simDatesData, getSimDatesData, updateSimDate } =
    useSimArtAdminProvider();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        await getSimDatesData();
      } catch (error: any) {
        const errorMessage =
          error?.message ||
          'An unexpected error occurred while fetching SIM data.';
        setErrMsg(errorMessage);
        setShowModal(true);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [getSimDatesData]);

  const columns: GridColDef[] = [
    {
      field: 'simYear',
      headerName: 'Year',
      width: 80,
      sortable: false,
      renderCell: (params: any) => {
        return <p className="sim-requests-admin-table__year">{params.value}</p>;
      }
    },
    {
      field: 'simMonth',
      headerName: 'Month',
      sortable: false,
      width: 110
    },
    {
      field: 'daterange',
      headerName: 'Date Range',
      width: 130,
      sortable: false
    },
    {
      field: 'simCloseDateTime',
      headerName: 'Submission Close',
      width: 120,
      sortable: false
    },
    {
      field: 'simSendDate',
      headerName: 'Send Dates',
      width: 150,
      sortable: false
    },
    {
      field: 'simStatus',
      headerName: 'Status',
      width: 160,
      renderCell: (params: any) => {
        const status = params.value;

        if (status === 'READY') {
          return (
            <div className="sim-requests-admin-table__ready">
              <p>READY</p>
            </div>
          );
        } else if (status === 'NOT READY') {
          return (
            <p className="sim-requests-admin-table__notready">NOT READY</p>
          );
        } else if (status === 'OPEN') {
          return (
            <div className="sim-requests-admin-table__open">
              <p>OPEN</p>
            </div>
          );
        } else if (status === 'CLOSED') {
          return (
            <div className="sim-requests-admin-table__closed">
              <p>CLOSED</p>
            </div>
          );
        }

        return <p>{status}</p>;
      },
      sortable: false
    },
    {
      field: 'commentsLength',
      headerName: 'Comments',
      flex: 1,
      renderCell: (params: any) => {
        const commentCount = params.value || 0;
        return (
          <div
            className="sim-requests-admin-table__comments"
            onClick={() => handleCommentsClick(params.row)}
            style={{ cursor: 'pointer' }}
          >
            <img src={NewsIcon} alt="news-icon" />
            <p>{commentCount === 0 ? '' : `${commentCount} Comments`}</p>
          </div>
        );
      },
      sortable: false
    },
    {
      field: 'modifiedDate',
      headerName: 'Last Modified',
      width: 200,
      sortable: false
    },
    {
      field: 'edit',
      headerName: '',
      width: 100,
      sortable: false,
      renderCell: (params) => {
        const hasOpenStatus = simDatesData?.some(
          (row: any) => row.simStatus === 'OPEN'
        );

        const hasReadyStatus = simDatesData?.some(
          (row: any) => row.simStatus === 'READY'
        );

        const closedRows =
          simDatesData?.filter((row: any) => row.simStatus === 'CLOSED') || [];

        const sortedClosedRows = closedRows.sort((a, b) => {
          const yearA = a.simYear;
          const yearB = b.simYear;
          if (yearA !== yearB) return yearB - yearA;

          const monthNames = [
            'January',
            'February',
            'March',
            'April',
            'May',
            'June',
            'July',
            'August',
            'September',
            'October',
            'November',
            'December'
          ];
          const monthA = monthNames.indexOf(a.simMonth);
          const monthB = monthNames.indexOf(b.simMonth);
          return monthB - monthA;
        });

        const lastClosedRow = sortedClosedRows[0];
        const isLastClosedMonth =
          lastClosedRow && params.row.id === lastClosedRow.id;

        let isDisabled = false;

        if (hasOpenStatus && params.row.simStatus === 'CLOSED') {
          isDisabled = true;
        } else if (params.row.simStatus === 'CLOSED' && hasReadyStatus) {
          isDisabled = !isLastClosedMonth;
        }

        return (
          <button
            className={`sim-requests-admin-table__edit-button ${isDisabled ? 'disabled' : ''}`}
            onClick={() => !isDisabled && handleEdit(params.row)}
            disabled={isDisabled}
          >
            <i className="icon-edit black" />
            <span className="sim-requests-admin-table__edit">Edit</span>
          </button>
        );
      }
    }
  ];

  const handleEdit = (row: any) => {
    setSelectedRowForEdit(row);
    setIsEditDrawerOpen(true);
  };

  const handleSaveEdit = async (updatedRow: any) => {
    setLoading(true);
    try {
      await updateSimDate(updatedRow.simRequestID, updatedRow);
      // Refresh the table data
      await getSimDatesData();
    } catch (error: any) {
      const errorMessage =
        error?.message ||
        'An unexpected error occurred while updating the SIM request.';
      setErrMsg(errorMessage);
      setShowModal(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCommentsClick = (row: any) => {
    setSelectedComments(row.comments || []);
    setSelectedRowForComments(row);
    setIsCommentsDialogOpen(true);
  };

  const handleCloseCommentsDialog = () => {
    setIsCommentsDialogOpen(false);
    setSelectedComments([]);
  };

  const getMonthNumber = (monthName: string): string => {
    const months = [
      '',
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December'
    ];
    const monthIndex = months.indexOf(monthName);
    return monthIndex > 0 ? monthIndex.toString() : '1';
  };

  const handleAddComment = async (commentText: string) => {
    if (!selectedRowForComments || !commentText.trim()) {
      return;
    }

    setCommentLoading(true);
    try {
      // When adding a comment, include existing values to satisfy backend validation
      // Use the raw API values to avoid any timezone conversion issues
      const updatedRowData = {
        simYear: selectedRowForComments.simYear,
        simMonth: getMonthNumber(selectedRowForComments.simMonth),
        simOpenDateTime: selectedRowForComments.simOpenDateTime,
        simCloseDateTime:
          selectedRowForComments.rawSimCloseDateTime ||
          selectedRowForComments.simCloseDate,
        simSendDate:
          selectedRowForComments.rawSimSendDate ||
          selectedRowForComments.simSendDate,
        comment: commentText,
        isCommentOnly: true // Flag to indicate this is a comment-only update
      };

      await updateSimDate(selectedRowForComments.id, updatedRowData);

      await getSimDatesData();
      handleCloseCommentsDialog();
    } catch (error: any) {
      const errorMessage =
        error?.message ||
        'An unexpected error occurred while adding the comment.';
      setErrMsg(errorMessage);
      setShowModal(true);
    } finally {
      setCommentLoading(false);
    }
  };

  if (loading) {
    return <Loader />;
  }

  if (showModal) {
    return (
      <>
        {showModal && (
          <AcDialog
            open={showModal}
            onClose={() => {
              setShowModal(false);
            }}
            data={{
              icon: 'icon-sad-emoji',
              title: 'Error',
              description: errMsg,
              actionLabel: 'Close',
              handleAction: () => {
                setShowModal(false);
              }
            }}
          />
        )}
      </>
    );
  }

  return (
    <>
      <div className="sim-requests-admin-title">
        <span className="title">{t('simRequestsAdmin.title')}</span>
      </div>
      <div className="sim-requests-admin-table-cntr">
        <DataGridPro
          autoHeight
          columns={columns}
          disableColumnMenu
          disableRowSelectionOnClick
          rows={simDatesData || []}
          hideFooterRowCount
        />
      </div>

      <EditSimRequestDrawer
        show={isEditDrawerOpen}
        setToggle={setIsEditDrawerOpen}
        selectedRow={selectedRowForEdit}
        onSave={handleSaveEdit}
        adjacentRanges={(function () {
          if (!selectedRowForEdit || !simDatesData?.length) return undefined;
          const index = simDatesData.findIndex(
            (r) => r.id === selectedRowForEdit.id
          );
          if (index === -1) return undefined;
          const prev = index > 0 ? simDatesData[index - 1] : undefined;
          const next =
            index < simDatesData.length - 1
              ? simDatesData[index + 1]
              : undefined;
          const prevRange =
            prev && prev.simCloseDate
              ? { open: prev.simOpenDateTime, close: prev.simCloseDate }
              : undefined;
          const nextRange =
            next && next.simCloseDate
              ? { open: next.simOpenDateTime, close: next.simCloseDate }
              : undefined;
          return { prev: prevRange, next: nextRange };
        })()}
      />

      <CommentsDialog
        open={isCommentsDialogOpen}
        onClose={handleCloseCommentsDialog}
        comments={selectedComments}
        onAddComment={handleAddComment}
        isAdding={commentLoading}
      />
    </>
  );
}
